/**
 * Per-process EPHEMERAL bpmn lane draft — the process tab's editable-view tier.
 *
 * A process's bpmn diagram is a synthesized view; this holds the lane/pool body
 * the user edits browser-only to reason about hand-offs, keyed by the tab's
 * `processKey` and persisted to localStorage so a reasoning session survives a
 * reload (mirrors the board's `useViewDrafts` draft persistence). Nothing here
 * touches the model — "Save as view" (ProcessPane) is the durable escalation.
 *
 * Incomplete rows are dropped on load so the draft-row array stays 1:1 with the
 * clauses the layout is built from (the rail editor's splice-by-index contract).
 */
import { useCallback, useEffect, useState } from "react";
import { flowBodyRowText, type FlowBodyRow } from "../views/clauseModel.js";

const KEY = (processKey: string): string => `arch:process-lane-draft:${processKey}`;

function load(processKey: string | null): readonly FlowBodyRow[] {
  if (!processKey || typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(KEY(processKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Keep only well-formed, COMPLETE rows (index-alignment contract).
    return parsed.filter((r): r is FlowBodyRow => {
      try {
        return flowBodyRowText(r as FlowBodyRow) !== null;
      } catch {
        return false;
      }
    });
  } catch {
    return [];
  }
}

export interface ProcessLaneDraft {
  readonly rows: readonly FlowBodyRow[];
  readonly setRows: (next: readonly FlowBodyRow[]) => void;
  readonly clear: () => void;
}

export function useProcessLaneDraft(processKey: string | null): ProcessLaneDraft {
  const [rows, setRowsState] = useState<readonly FlowBodyRow[]>(() => load(processKey));

  // Re-hydrate when the active process changes (each process its own draft).
  useEffect(() => {
    setRowsState(load(processKey));
  }, [processKey]);

  const persist = useCallback(
    (next: readonly FlowBodyRow[]): void => {
      if (!processKey || typeof localStorage === "undefined") return;
      try {
        if (next.length === 0) localStorage.removeItem(KEY(processKey));
        else localStorage.setItem(KEY(processKey), JSON.stringify(next));
      } catch {
        /* storage full / unavailable — the in-memory draft still works */
      }
    },
    [processKey],
  );

  const setRows = useCallback(
    (next: readonly FlowBodyRow[]): void => {
      setRowsState(next);
      persist(next);
    },
    [persist],
  );

  const clear = useCallback((): void => setRows([]), [setRows]);

  return { rows, setRows, clear };
}
